/**
 * useBridge Hook - Main Bridge Orchestration
 *
 * Orchestrates the complete CCTP bridge flow:
 * 1. Manages bridge state (chains, amount, fast mode)
 * 2. Fee estimation via backend API
 * 3. Burn execution via useBurn hook
 * 4. Backend submission and mint tracking via useMint hook
 *
 * This is the main hook used by BridgePage.
 */

import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import type {
  BridgeState,
  Chain,
  FeeEstimate,
  Transfer,
  TransferStatus,
} from '@/types'
import { bridgeApi, walletApi, intentApi } from '@/services/api'
import { useBurn, type BurnStep } from './useBurn'
import type { Chain as SharedChain } from '@parse/shared'
import { useMint } from './useMint'
import { useRecovery, type PendingTransfer } from './useRecovery'
import {
  createPendingBurn,
  markBurnConfirmed,
  markBurnSubmitted,
  markSubmissionFailed,
  updateBackendStatus,
  updateBurnIntentId,
  removeBurn,
  getBurnByTxHash,
  type StoredBurn,
} from '@/utils/burnStorage'
import { getErrorMessage, isUserRejection, getDisplayError } from '@/utils/errors'
import { toast } from '@/utils/toast'
import { log } from '@/utils/log'
import type { RecipientRiskConfirm } from '@/utils/recipientCodeGuard'

// Retry configuration for backend submission
const MAX_SUBMISSION_ATTEMPTS = 5
const INITIAL_RETRY_DELAY_MS = 2000 // 2 seconds

/**
 * Outcome of registering a burn with the backend (G1/G2).
 *
 * A boolean cannot express this: `blocked` and `failed` are both "not submitted" but they need
 * opposite user-facing statements. `failed` genuinely IS resumable — the pending burn is in
 * localStorage and the recovery path can resubmit it. `blocked` is NOT: every resubmit re-screens
 * fresh and returns the same 451 forever. Telling a blocked user their burn "can be resumed" is the
 * same class of false assertion as telling a DEX user their funds were refunded.
 */
type SubmissionOutcome = 'submitted' | 'blocked' | 'failed'

/**
 * Sleep helper for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// Transfer step - combined view of burn and mint progress
export type TransferStep =
  | 'idle'
  | 'switching_network'
  | 'approving'   // ERC-20 approve tx — contract-account bridge() path only (#137 Phase 1)
  | 'signing'
  | 'burning'
  | 'confirming'
  | 'attesting'
  | 'minting'
  | 'complete'
  | 'error'

interface UseBridgeReturn {
  // Bridge state
  state: BridgeState
  feeEstimate: FeeEstimate | null
  isEstimating: boolean

  // State setters
  setSourceChain: (chain: Chain | null) => void
  setDestinationChain: (chain: Chain | null) => void
  setDestinationAddress: (address: string, manual?: boolean) => void
  setAmount: (amount: string) => void
  setPayNative: (enabled: boolean) => void
  swapDirection: () => void

  // Fee estimation
  estimateFees: (userAddress?: string) => Promise<void>

  // Bridge execution
  executeBridge: (
    sourceAddress: string,
    destinationAddress: string,
    confirmRecipientRisk: RecipientRiskConfirm,
  ) => Promise<string>

  // Combined transfer state
  isTransferring: boolean
  transferStep: TransferStep
  error: string | null

  // Burn state (from useBurn)
  burnTxHash: string | null
  burnStep: BurnStep
  burnError: string | null

  // Mint state (from useMint)
  mintStatus: TransferStatus | null
  mintTxHash: string | null
  mintError: string | null
  transfer: Transfer | null

  // Recovery state
  pendingTransfers: PendingTransfer[]
  isRecoveryLoading: boolean
  resumeTransfer: (burnTxHash: string) => void
  dismissTransfer: (burnTxHash: string) => void
  dismissAllTransfers: () => void
  triggerRecoveryCheck: (address: string) => void

  // Reset
  reset: () => void
}

const initialState: BridgeState = {
  sourceChain: null,
  destinationChain: null,
  destinationAddress: '',
  destManuallyChosen: false,
  amount: '',
  payNative: false,
}

export function useBridge(): UseBridgeReturn {
  // Bridge state
  const [state, setState] = useState<BridgeState>(initialState)
  const [feeEstimate, setFeeEstimate] = useState<FeeEstimate | null>(null)
  const [isEstimating, setIsEstimating] = useState(false)
  const [bridgeError, setBridgeError] = useState<string | null>(null)

  // Child hooks for burn, mint, and recovery
  const burn = useBurn()
  const mint = useMint()
  const recovery = useRecovery()

  // Use ref to access current state without causing dependency changes
  const stateRef = useRef<BridgeState>(state)
  stateRef.current = state

  // Track current intentId for mint completion updates
  const currentIntentIdRef = useRef<string | null>(null)

  // ============================================
  // State Setters
  // ============================================

  const setSourceChain = useCallback((chain: Chain | null) => {
    setState(prev => ({ ...prev, sourceChain: chain }))
    setFeeEstimate(null)
  }, [])

  const setDestinationChain = useCallback((chain: Chain | null) => {
    setState(prev => ({
      ...prev,
      destinationChain: chain,
    }))
    setFeeEstimate(null)
  }, [])

  // `manual` = the user explicitly chose this recipient (RecipientModal), so it must NOT be overwritten by
  // the wallet-sync effect and MUST win in the recipient derivation. An empty address always clears the flag
  // (a disconnect / clear reverts to the self-bridge default). Callers that auto-fill from the connected
  // wallet omit `manual` (defaults false).
  const setDestinationAddress = useCallback((address: string, manual = false) => {
    setState(prev => ({ ...prev, destinationAddress: address, destManuallyChosen: manual && !!address }))
  }, [])

  const setAmount = useCallback((amount: string) => {
    setState(prev => ({ ...prev, amount }))
    setFeeEstimate(null)
  }, [])

  const setPayNative = useCallback((enabled: boolean) => {
    setState(prev => ({ ...prev, payNative: enabled }))
  }, [])

  const swapDirection = useCallback(() => {
    log.debug('BRIDGE_SWAP_DIRECTION_CALLED', {
      beforeSwap: {
        sourceChain: stateRef.current.sourceChain,
        destinationChain: stateRef.current.destinationChain,
      }
    })

    setState(prev => {
      const newState = {
        ...prev,
        sourceChain: prev.destinationChain,
        destinationChain: prev.sourceChain,
        destinationAddress: '', // cleared — BridgePage effect will re-populate from wallet
        destManuallyChosen: false, // a direction swap redefines the dest → drop any explicit choice
      }

      log.debug('BRIDGE_SWAP_DIRECTION_COMPLETE', {
        afterSwap: {
          sourceChain: newState.sourceChain,
          destinationChain: newState.destinationChain,
        }
      })

      return newState
    })
    setFeeEstimate(null)
  }, [])

  // ============================================
  // Fee Estimation
  // ============================================

  const estimateFees = useCallback(async (userAddress?: string) => {
    const { amount, sourceChain, destinationChain } = stateRef.current

    if (!amount || !sourceChain || !destinationChain) {
      return
    }

    setIsEstimating(true)
    setBridgeError(null)

    try {
      const response = await bridgeApi.estimateFee(
        amount,
        sourceChain,
        destinationChain,
        userAddress
      )

      if (response.success && response.data) {
        setFeeEstimate(response.data)
      } else {
        const errorCode = response.error?.code
        if (errorCode === 'REFERRER_EXPIRED' || errorCode === 'REFERRER_BLOCKED') {
          localStorage.removeItem('parsebridge:ref')
          localStorage.removeItem('parsebridge:ref_sol')
          toast.info(errorCode === 'REFERRER_EXPIRED'
            ? 'Your referrer link has expired.'
            : 'Your referrer is no longer active.')
        }
        setBridgeError(response.error?.message
          ? getDisplayError(response.error.message)
          : 'Failed to estimate fees')
      }
    } catch (e) {
      setBridgeError(getDisplayError(e) || 'Failed to estimate fees')
    } finally {
      setIsEstimating(false)
    }
  }, [])

  // ============================================
  // Bridge Execution
  // ============================================

  /**
   * Submit burn to backend with exponential backoff retry.
   *
   * ── G1: NOT every failure here is retryable, and one of them is not resumable either ───────────
   * `/hybrid` screens both addresses with `forceFresh = true` (cache bypassed) BEFORE the dedupe,
   * BEFORE burn validation and BEFORE the ownership bind — it is the FIRST post-burn gate, not the
   * fourth. Registration is always post-burn, so if a list refresh flags either address between
   * quote issuance and registration (or on a recovery resubmit days later) the USDC is already
   * burned and the endpoint returns 451 permanently.
   *
   * Retrying that is worse than useless: every attempt re-screens fresh, so five attempts mean five
   * `forceFresh` provider calls per burn and five identical 451s. Then the caller told the user
   * "Your burn is saved and can be resumed" — presenting a compliance decision as a transient
   * backend blip, and inviting a resume that can only ever 451 again.
   *
   * So the result is a discriminated outcome rather than a boolean: 451 stops immediately and is
   * reported as its own thing.
   */
  const submitToBackendWithRetry = useCallback(
    async (
      pendingBurn: StoredBurn,
      burnTxHash: string,
      params: {
        amount: string
        sourceChain: Chain
        destinationChain: Chain
        sourceAddress: string
        destinationAddress: string
        networkFeeUsdc?: string
        nativePriceUsd?: number
      }
    ): Promise<SubmissionOutcome> => {
      for (let attempt = 1; attempt <= MAX_SUBMISSION_ATTEMPTS; attempt++) {
        try {
          log.debug(`Backend submission attempt ${attempt}/${MAX_SUBMISSION_ATTEMPTS}...`)

          const response = await bridgeApi.initiateHybridTransfer({
            amount: params.amount,
            sourceChain: params.sourceChain,
            destinationChain: params.destinationChain,
            sourceAddress: params.sourceAddress,
            destinationAddress: params.destinationAddress,
            burnTxHash,
            networkFeeUsdc: params.networkFeeUsdc,
            nativePriceUsd: params.nativePriceUsd,
            ref: localStorage.getItem('parsebridge:ref') || undefined,
          })

          if (response.success) {
            // Success! Mark as submitted and remove from localStorage.
            // NOTE this now also covers the 409 "Transfer already registered" case, which
            // `initiateHybridTransfer` maps to success (G2) — a transfer that DID register must not
            // be reported to the user as a failure.
            markBurnSubmitted(pendingBurn.id)
            removeBurn(pendingBurn.id)
            log.debug('Backend accepted transfer:', response.data?.transferId)
            return 'submitted'
          }

          // G1 — a 451 is a permanent compliance decision, not a transient backend problem. Stop
          // now: retrying burns five fresh sanctions-provider lookups to receive the same answer.
          if (response.status === 451 || response.error?.code === 'ADDRESS_SANCTIONED') {
            const message = response.error?.message || 'Transfer blocked by compliance screening'
            markSubmissionFailed(pendingBurn.id, message, attempt)
            log.error('Backend rejected transfer on compliance grounds — not retrying')
            return 'blocked'
          }

          // API returned error - throw to trigger retry
          throw new Error(response.error?.message || 'Backend rejected submission')
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : 'Unknown error'
          log.warn('Submission attempt failed:', { attempt, errorMsg })

          if (attempt === MAX_SUBMISSION_ATTEMPTS) {
            // Final attempt failed - mark as failed in storage
            markSubmissionFailed(pendingBurn.id, errorMsg, attempt)
            log.error(`Backend submission failed after ${attempt} attempts`)
            return 'failed'
          }

          // Exponential backoff: 2s, 4s, 8s, 16s, 32s
          const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1)
          log.debug(`Retrying in ${delay / 1000}s...`)
          await sleep(delay)
        }
      }
      return 'failed'
    },
    []
  )

  const executeBridge = useCallback(
    async (
      sourceAddress: string,
      destinationAddress: string,
      // #144 — threaded rather than defaulted. `BurnParams.confirmRecipientRisk` is a REQUIRED field,
      // so omitting it here is a type error, not a silently-disabled guard.
      confirmRecipientRisk: RecipientRiskConfirm,
    ): Promise<string> => {
      const { amount, sourceChain, destinationChain, payNative } = stateRef.current

      // Validate required fields
      if (!amount || parseFloat(amount) <= 0) {
        throw new Error('Invalid amount')
      }
      if (!sourceChain) {
        throw new Error('Source chain not selected')
      }
      if (!destinationChain) {
        throw new Error('Destination chain not selected')
      }
      if (!sourceAddress) {
        throw new Error('Source address not provided')
      }
      if (!destinationAddress) {
        throw new Error('Destination address not provided')
      }
      if (sourceChain === destinationChain) {
        throw new Error('Source and destination chains must be different')
      }

      setBridgeError(null)

      // Clear any PRIOR transfer's terminal mint status before this burn. A lingering 'mint_success' from a
      // previous CCTP leg would otherwise leak into transferStep's fallback (useBridge terminal branch) and
      // flash a false "Transfer Complete" in the window between this burn completing and its first mint poll
      // (reported on base→sol→base). The DEX engine self-resets at the top of executeDexBridge; the CCTP
      // engine did not — this is that missing reset. mint is already in this callback's deps.
      mint.reset()

      log.debug('Starting bridge transfer:', {
        sourceChain,
        destinationChain,
        amount,
        sourceAddress: sourceAddress.slice(0, 10) + '...',
        destinationAddress: destinationAddress.slice(0, 10) + '...',
      })

      // ============================================
      // Step -1: Save intent to DB BEFORE wallet popup
      // This ensures we can recover even if everything else fails
      // ============================================
      let intentId: string | null = null
      let dbIntentRequired = true // Default to true (safer)

      try {
        log.debug('Step -1: Saving transfer intent...')
        const intentResponse = await intentApi.trackIntent({
          sourceAddress,
          destinationAddress,
          sourceChain,
          destinationChain,
          amount,
        })

        // Non-bypassable sanctions block — regardless of dbIntentRequired setting
        if (!intentResponse.success && intentResponse.error?.code === 'ADDRESS_SANCTIONED') {
          throw new Error('This address cannot use this service due to compliance requirements.')
        }

        if (intentResponse.success && intentResponse.data) {
          intentId = intentResponse.data.intentId
          dbIntentRequired = intentResponse.data.dbIntentRequired
          log.debug('Intent saved:', intentId.slice(0, 8) + '...')
        } else {
          // Intent save failed
          dbIntentRequired = intentResponse.data?.dbIntentRequired ?? true
          log.warn('Intent save failed:', intentResponse.error?.message)

          if (dbIntentRequired) {
            // Block the user - must save intent before proceeding
            throw new Error('Failed to initialize transfer. Please try again.')
          }
          // Continue with localStorage fallback
          log.debug('Continuing with localStorage fallback (DB_INTENT_REQUIRED=false)')
        }
      } catch (intentError) {
        // Network error or other failure
        const errorMessage = getErrorMessage(intentError)
        if (errorMessage === 'Failed to initialize transfer. Please try again.') {
          setBridgeError(errorMessage)
          throw intentError // Re-throw our blocking error
        }
        if (errorMessage.includes('compliance requirements')) {
          setBridgeError(errorMessage)
          throw intentError // Re-throw sanctions block with original message
        }
        log.warn('Intent tracking error:', errorMessage)
        // If we can't determine dbIntentRequired, default to blocking for safety
        const fallbackMsg = 'Failed to initialize transfer. Please try again.'
        setBridgeError(fallbackMsg)
        throw new Error(fallbackMsg)
      }

      // Step 0: Save pending burn to localStorage BEFORE burn TX
      // This captures user intent so we can recover if anything fails
      const pendingBurn = createPendingBurn({
        sourceChain: sourceChain!,
        destinationChain: destinationChain!,
        sourceAddress,
        destinationAddress,
        amount,
      })
      log.debug('Pending burn saved:', pendingBurn.id)

      // Link intentId to pending burn for recovery after page refresh
      if (intentId) {
        updateBurnIntentId(pendingBurn.id, intentId)
      }

      try {
        // Step 1: Execute burn transaction (user signs in wallet)
        log.debug('Step 1: Executing burn...')
        const { txHash: burnTxHash, networkFeeUsdc, nativePriceUsd } = await burn.executeBurn({
          sourceChain: sourceChain as SharedChain,
          amount,
          destinationChain: destinationChain as SharedChain,
          destinationAddress,
          payNative,
          confirmRecipientRisk,
        })

        log.debug('Burn complete:', burnTxHash)

        // Update localStorage with burn tx hash
        markBurnConfirmed(pendingBurn.id, burnTxHash)

        // Update intent with burn tx hash
        if (intentId) {
          try {
            await intentApi.updateIntent(intentId, 'burned', burnTxHash)
            log.debug('Intent updated: burned')
          } catch (updateError) {
            log.warn('Failed to update intent status:', updateError)
          }
        }

        // Track wallet transfer attempt (after burn confirms)
        // This captures the user has committed to the transfer
        try {
          await walletApi.trackTransferAttempt({
            address: sourceAddress,
            chainType: sourceChain === 'solana' ? 'solana' : 'evm',
            chainUsed: sourceChain,
            amount,
            destinationAddress,
          })
          log.debug('Wallet transfer attempt tracked')
        } catch (trackError) {
          // Non-fatal - don't block the transfer if tracking fails
          log.warn('Failed to track wallet attempt:', trackError)
        }

        // Step 2: Submit to backend with retry logic
        log.debug('Step 2: Submitting to backend with retry...')
        const submitted = await submitToBackendWithRetry(pendingBurn, burnTxHash, {
          amount,
          sourceChain,
          destinationChain,
          sourceAddress,
          destinationAddress,
          networkFeeUsdc,
          nativePriceUsd,
        })

        // Update intent to submitted
        if (intentId && submitted === 'submitted') {
          try {
            await intentApi.updateIntent(intentId, 'submitted')
            log.debug('Intent updated: submitted')
          } catch (updateError) {
            log.warn('Failed to update intent status:', updateError)
          }
        }

        if (submitted === 'blocked') {
          // G1 — a compliance rejection. The burn HAPPENED; what failed is registration, and it
          // will fail identically on every retry because the endpoint re-screens fresh each time.
          // Saying "can be resumed" here would send the user round a loop that cannot terminate.
          setBridgeError(
            'This transfer could not be completed because the destination or source address did ' +
            'not pass a compliance check. Retrying will not change this. Please contact support ' +
            `with your transaction hash (${burnTxHash}).`
          )
          return burnTxHash
        }

        if (submitted === 'failed') {
          // Genuinely transient (network, 5xx, RPC blip during burn validation). This one IS
          // resumable: the pending burn is in localStorage and the recovery path can resubmit it.
          setBridgeError('Failed to submit to backend. Your burn is saved and can be resumed.')
          // Still return the hash so UI can show it
          return burnTxHash
        }

        // Step 3: Start polling for mint status
        log.debug('Step 3: Starting mint status polling...')
        // Store intentId in a ref so mint completion effect can access it
        currentIntentIdRef.current = intentId
        mint.pollMintStatus(burnTxHash)

        return burnTxHash
      } catch (e) {
        const errorMsg = getErrorMessage(e)

        // Check if this was a user rejection (don't keep pending burn)
        const userRejected = isUserRejection(e)

        if (userRejected) {
          // User cancelled - remove the pending burn and update intent
          removeBurn(pendingBurn.id)

          // Update intent to cancelled
          if (intentId) {
            try {
              await intentApi.updateIntent(intentId, 'cancelled')
              log.debug('Intent updated: cancelled')
            } catch (updateError) {
              log.warn('Failed to update intent status:', updateError)
            }
          }

          // Don't show error for user cancellation - just reset state
          setBridgeError(null)
          return '' // Return empty string for cancelled transfer
        }
        // Otherwise leave the pending burn in storage for recovery

        setBridgeError(errorMsg)
        throw e
      }
    },
    [burn, mint, submitToBackendWithRetry]
  )

  // ============================================
  // Recovery Effects
  // ============================================

  // Trigger recovery check - called by BridgePage when wallet connects
  const triggerRecoveryCheck = useCallback((address: string) => {
    log.debug('Triggering recovery check for:', address.slice(0, 10))
    recovery.checkWalletAndRecover(address)
  }, [recovery.checkWalletAndRecover])

  // Update localStorage and intent when mint status changes
  useEffect(() => {
    if (burn.burnTxHash && mint.mintStatus) {
      // Find the burn by tx hash and update its backend status
      const storedBurn = getBurnByTxHash(burn.burnTxHash)
      if (storedBurn) {
        updateBackendStatus(storedBurn.id, mint.mintStatus)

        // On successful mint, track wallet success, update intent, and remove from localStorage
        if (mint.mintStatus === 'mint_success' || mint.mintStatus === 'recovered') {
          // Update intent to completed (non-blocking)
          // Use ref if available, otherwise fallback to stored intentId (survives page refresh)
          const intentId = currentIntentIdRef.current || storedBurn.intentId
          if (intentId) {
            intentApi.updateIntent(intentId, 'completed').then(() => {
              log.debug('Intent updated: completed')
              currentIntentIdRef.current = null // Clear the ref
            }).catch((err) => {
              log.warn('Failed to update intent to completed:', err)
            })
          }

          // Track wallet transfer success (non-blocking)
          walletApi.trackTransferSuccess({
            address: storedBurn.sourceAddress,
            amount: storedBurn.amount,
            destinationAddress: storedBurn.destinationAddress,
          }).then(() => {
            log.debug('Wallet transfer success tracked')
          }).catch((err) => {
            log.warn('Failed to track wallet success:', err)
          })

          removeBurn(storedBurn.id)
        }
      }
    }
  }, [burn.burnTxHash, mint.mintStatus])

  // Resume tracking a pending transfer
  const handleResumeTransfer = useCallback((burnTxHash: string) => {
    recovery.resumeTransfer(burnTxHash)
    mint.pollMintStatus(burnTxHash)
  }, [recovery, mint])

  // ============================================
  // Computed State
  // ============================================

  // Combined transferring state - only true during burn phase
  // This controls the Send button - user can start new transfer after burn is submitted
  const isTransferring = burn.isBurning

  // Combined transfer step
  const transferStep: TransferStep = useMemo(() => {
    // Check for errors first
    if (burn.burnStep === 'error' || mint.mintError) {
      return 'error'
    }

    // Burn phase
    if (burn.burnStep === 'switching_network') return 'switching_network'
    // Contract-account path only — an EOA burn never emits 'approving', so this line is inert for it.
    if (burn.burnStep === 'approving') return 'approving'
    if (burn.burnStep === 'signing') return 'signing'
    if (burn.burnStep === 'burning') return 'burning'

    // If burn is complete and we're polling
    if (burn.burnStep === 'complete' && mint.isPolling) {
      // Map mint status to transfer step
      const status = mint.mintStatus
      if (!status || status === 'burn_submitted') {
        return 'confirming'
      }
      // burn_success means the source tx is ALREADY confirmed on-chain — from here we're in the cross-chain /
      // Circle-attestation phase. Map it (not just attestation_*) to 'attesting' so: (1) the long attestation
      // wait shows on "Moving between chains" instead of hanging on "Confirming on {source}", and (2) the bridge
      // step stays active even when the attestation_* sub-states race past faster than the FE poll interval (fast
      // transfers), which previously made "Moving between chains" flash by or appear skipped.
      if (status === 'burn_success' || status === 'attestation_pending' || status === 'attestation_received') {
        return 'attesting'
      }
      if (status === 'mint_submitted') {
        return 'minting'
      }
      if (status === 'mint_success' || status === 'recovered') {
        return 'complete'
      }
      if (status === 'failed') {
        return 'error'
      }
      return 'confirming'
    }

    // Check if complete
    if (mint.mintStatus === 'mint_success' || mint.mintStatus === 'recovered') {
      return 'complete'
    }

    return 'idle'
  }, [burn.burnStep, mint.isPolling, mint.mintStatus, mint.mintError])

  // Combined error
  const error = bridgeError || burn.burnError || mint.mintError

  // ============================================
  // Reset
  // ============================================

  const reset = useCallback(() => {
    // Reset child hooks
    burn.reset()
    mint.reset()

    // Reset local state
    setState(prev => ({ ...prev, amount: '' }))
    setFeeEstimate(null)
    setBridgeError(null)
  }, [burn, mint])

  // ============================================
  // Return
  // ============================================

  return {
    // Bridge state
    state,
    feeEstimate,
    isEstimating,

    // State setters
    setSourceChain,
    setDestinationChain,
    setDestinationAddress,
    setAmount,
    setPayNative,
    swapDirection,

    // Fee estimation
    estimateFees,

    // Bridge execution
    executeBridge,

    // Combined transfer state
    isTransferring,
    transferStep,
    error,

    // Burn state
    burnTxHash: burn.burnTxHash,
    burnStep: burn.burnStep,
    burnError: burn.burnError,

    // Mint state
    mintStatus: mint.mintStatus,
    mintTxHash: mint.mintTxHash,
    mintError: mint.mintError,
    transfer: mint.transfer,

    // Recovery state
    pendingTransfers: recovery.pendingTransfers,
    isRecoveryLoading: recovery.isLoading,
    resumeTransfer: handleResumeTransfer,
    dismissTransfer: recovery.dismissTransfer,
    dismissAllTransfers: recovery.clearAll,
    triggerRecoveryCheck,

    // Reset
    reset,
  }
}
